import { type ReactNode, useCallback, useRef } from "react";
import type { Key } from "react-aria-components";
import { cn } from "cn";
import { Select, SelectContent, SelectTrigger, type SelectTriggerProps } from "@/components/ui/select.tsx";
import { CLEARABLE_TRIGGER_CLASSNAME, filterChipStyles } from "./FilterChip.tsx";
import { FilterChipClearButton } from "./FilterChipClearButton.tsx";

const TRIGGER_CLASSNAME = "cursor-pointer sm:px-3 focus:ring-0 group-open/select:ring-0 [&_[data-slot=chevron]]:hidden";

const ACTIVE_TRIGGER_CLASSNAME = CLEARABLE_TRIGGER_CLASSNAME;

const POPOVER_PROPS = { placement: "bottom start" as const };

export interface FilterChipSelectProps {
    ariaLabel: string;
    clearAriaLabel: string;
    selectedKey: Key;
    defaultKey: Key;
    onSelectionChange: (key: Key) => void;
    prefix?: SelectTriggerProps["prefix"];
    children: ReactNode;
    isDisabled?: boolean;
}

export function FilterChipSelect({ ariaLabel, clearAriaLabel, selectedKey, defaultKey, onSelectionChange, prefix, children, isDisabled }: FilterChipSelectProps) {
    const isActive = selectedKey !== defaultKey;
    const wrapperRef = useRef<HTMLDivElement>(null);
    const handleClear = useCallback(() => {
        wrapperRef.current?.querySelector<HTMLButtonElement>('[data-slot="control"] button')?.focus();
        onSelectionChange(defaultKey);
    }, [onSelectionChange, defaultKey]);
    const handleSelectionChange = useCallback(
        (key: Key | null) => {
            if (key !== null) {
                onSelectionChange(key);
            }
        },
        [onSelectionChange]
    );

    return (
        <div ref={wrapperRef} className="flex items-center">
            <Select aria-label={ariaLabel} selectedKey={selectedKey} onSelectionChange={handleSelectionChange} isDisabled={isDisabled} className="w-auto">
                <SelectTrigger prefix={prefix} className={cn(filterChipStyles({ isActive }), TRIGGER_CLASSNAME, isActive && ACTIVE_TRIGGER_CLASSNAME)} />
                <SelectContent popover={POPOVER_PROPS}>{children}</SelectContent>
            </Select>
            {isActive && <FilterChipClearButton ariaLabel={clearAriaLabel} onClear={handleClear} isDisabled={isDisabled} />}
        </div>
    );
}
